import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { extractErrorMessage } from '../utils/errorHandler';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AutocompleteField from '../components/AutocompleteField';
import { ProductForm, Product } from '../types';
import ProductFilters from '../components/ProductFilters';
import { MultiSelect } from "react-multi-select-component";
import './ProductEditor.css';

// Roughly what fits on a slide; exceeding it only warns, saving still works.
const CHARACTERISTICS_SOFT_LIMIT = 350;

const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
  // Prevent form submission when Enter is pressed in input fields
  if (e.key === 'Enter') {
    e.preventDefault();
  }
};

const CustomItemRenderer = ({
  checked,
  option,
  onClick,
  disabled,
}: {
  checked: boolean;
  option: { label: string; value: any };
  onClick: () => void;
  disabled?: boolean;
}) => {
  return (
    <div
      className={disabled ? 'ms-item ms-item--disabled' : 'ms-item'}
      onClick={onClick} // This should trigger for the entire row
    >
      <span className="ms-item__label">{option.label}</span>
      <div className="ms-item__check">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          readOnly

          onClick={(e) => e.stopPropagation()} // Prevent duplicate clicks
          onKeyDown={(e) => e.key === " " && onClick()}
        />
      </div>
    </div>
  );
};

const ProductEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const [product, setProduct] = useState<ProductForm>({
    name: '',
    product_type_id: 1,
    category_id: undefined,
    subcategory_id: undefined,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('');

  // For awards functionality
  const [awards, setAwards] = useState<string[]>([]);
  const [currentAward, setCurrentAward] = useState('');

  // State for filters
  const [filters, setFilters] = useState({
    name: '',
    brand: '',
    country: '',
    region: '',
    product_type_id: '',
    productType: 'all',
    category: '',
    category_id: '',
    subcategory_id: '',
    vintage: '',
    alcoholMin: '',
    alcoholMax: '',
    userId: 'all',
    grape_variety: '',
    characteristics: ''
  });

  // Add state for category/subcategory options
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState<any[]>([]);
  const [productTypes, setProductTypes] = useState<any[]>([]);

  // Add these state variables for new category/subcategory functionality
  const [addingNewCategory, setAddingNewCategory] = useState(false);
  const [addingNewSubcategory, setAddingNewSubcategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingSubcategory, setSavingSubcategory] = useState(false);

  // Add these state variables for product features
  const [productFeatures, setProductFeatures] = useState<any[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<any[]>([]);
  const [addingNewFeature, setAddingNewFeature] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const [savingFeature, setSavingFeature] = useState(false);

  // --- State for Grape Varieties (ensure these are present) ---
  const [grapeVarieties, setGrapeVarieties] = useState<any[]>([]);
  const [selectedGrapeVarieties, setSelectedGrapeVarieties] = useState<any[]>([]);
  const [addingNewGrapeVariety, setAddingNewGrapeVariety] = useState(false);
  const [newGrapeVariety, setNewGrapeVariety] = useState('');
  const [savingGrapeVariety, setSavingGrapeVariety] = useState(false);

  // --- ALL HOOKS AND FUNCTIONS MOVED BEFORE CONDITIONAL RETURNS ---

  const fetchProduct = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');

      const response = await axios.get(`/api/products/${id}`);
      console.log('Product data received:', response.data);

      const productData = response.data;
      setProduct({
        name: productData.name || '',
        product_type_id: productData.product_type_id || 1,
        category_id: productData.category_id || undefined,
        subcategory_id: productData.subcategory_id || undefined,
        brand: productData.brand || '',
        country: productData.country || '',
        region: productData.region || '',
        appellation: productData.appellation || '',
        grape_variety: productData.grape_variety || '',
        alcohol_content: productData.alcohol_content || undefined,
        vintage: productData.vintage || undefined,
        volume: productData.volume || undefined,
        taste_profile: productData.taste_profile || '',
        characteristics: productData.characteristics || '',
        price_net: productData.price_net || undefined,
        price_alt: productData.price_alt || undefined,
        image_url: productData.image_url || '',
        is_active: productData.is_active !== undefined ? productData.is_active : true,
        // Add any missing fields
        awards: productData.awards || [],
        wrapper: productData.wrapper || '',
        length_mm: productData.length_mm || undefined,
        ring_gauge: productData.ring_gauge || undefined,
        age_years: productData.age_years || undefined // Add this line
      });

      // Fix for loading awards
      if (productData.awards) {
        // Handle both string and array formats
        if (typeof productData.awards === 'string') {
          try {
            // Try to parse if it's a JSON string
            const parsedAwards = JSON.parse(productData.awards);
            setAwards(Array.isArray(parsedAwards) ? parsedAwards : []);
          } catch {
            // If parsing fails, treat it as a single award
            setAwards([productData.awards]);
          }
        } else if (Array.isArray(productData.awards)) {
          // If it's already an array, use it directly
          setAwards(productData.awards);
        }
      } else {
        setAwards([]);
      }

      if (productData.image_url) {
        setUploadedImageUrl(productData.image_url);
      }

      // Add this code after you set the product state
      if (productData.features && productData.features.length > 0) {
        const featureOptions = productData.features.map((feature: string) => ({
          value: feature,
          label: feature
        }));
        setSelectedFeatures(featureOptions);
      } else {
        setSelectedFeatures([]);
      }

      // Add this code to set grape varieties
      if (productData.grape_variety && productData.grape_variety.length > 0) {
        const grapeOptions = productData.grape_variety.map((grape: string) => ({
          value: grape,
          label: grape
        }));
        setSelectedGrapeVarieties(grapeOptions);
      } else {
        setSelectedGrapeVarieties([]);
      }

    } catch (err: any) {
      console.error('Error fetching product:', err);
      console.error('Error response:', err.response?.data);
      setError(`Nie udało się pobrać danych produktu: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Add new category handler
  const handleAddNewCategory = async () => {
    if (!newCategoryName.trim() || !product.product_type_id) {
      return;
    }

    try {
      setSavingCategory(true);
      const response = await axios.post('/api/categories', {
        name: newCategoryName.trim(),
        product_type_id: product.product_type_id
      });

      // Add the new category to the list
      const newCategory = response.data;
      setCategoryOptions(prevOptions => [...prevOptions, newCategory]);

      // Select the new category
      setProduct(prev => ({
        ...prev,
        category_id: newCategory.id,
        subcategory_id: undefined // Reset subcategory when changing category
      }));

      // Reset the form
      setNewCategoryName('');
      setAddingNewCategory(false);
    } catch (err: any) {
      console.error('Error creating new category:', err);
      setError(`Nie udało się utworzyć nowej kategorii: ${extractErrorMessage(err)}`);
    } finally {
      setSavingCategory(false);
    }
  };

  // Add new subcategory handler
  const handleAddNewSubcategory = async () => {
    if (!newSubcategoryName.trim() || !product.category_id) {
      return;
    }

    try {
      setSavingSubcategory(true);
      const response = await axios.post('/api/subcategories', {
        name: newSubcategoryName.trim(),
        category_id: product.category_id
      });

      // Add the new subcategory to the list
      const newSubcategory = response.data;
      setSubcategoryOptions(prevOptions => [...prevOptions, newSubcategory]);

      // Select the new subcategory
      setProduct(prev => ({
        ...prev,
        subcategory_id: newSubcategory.id
      }));

      // Reset the form
      setNewSubcategoryName('');
      setAddingNewSubcategory(false);
    } catch (err: any) {
      console.error('Error creating new subcategory:', err);
      setError(`Nie udało się utworzyć nowej podkategorii: ${extractErrorMessage(err)}`);
    } finally {
      setSavingSubcategory(false);
    }
  };

  // Add this function with your other fetch functions
const fetchProductFeatures = useCallback(async (productTypeId: number) => {
  if (!productTypeId) {
    setProductFeatures([]);
    return;
  }

  try {
    const response = await axios.get(`/api/product-features?product_type_id=${productTypeId}`);
    setProductFeatures(response.data || []);
  } catch (err) {
    console.error('Error fetching product features:', err);
    setProductFeatures([]);
  }
}, []);

  // Add a function to handle adding a new feature
  const handleAddNewFeature = async () => {
    if (!newFeature.trim() || !product.product_type_id) {
      return;
    }

    try {
      setSavingFeature(true);
      const response = await axios.post('/api/product-features', {
        name: newFeature.trim(),
        product_type_id: product.product_type_id,
        description: ''
      });

      // Add the new feature to the list
      const newFeatureData = response.data;
      setProductFeatures(prev => [...prev, newFeatureData]);

      // Select the new feature
      const newFeatureOption = {
        value: newFeatureData.name,
        label: newFeatureData.name
      };
      setSelectedFeatures(prev => [...prev, newFeatureOption]);

      // Reset the form
      setNewFeature('');
      setAddingNewFeature(false);
    } catch (err: any) {
      console.error('Error creating new feature:', err);
      setError(`Nie udało się utworzyć nowej cechy: ${extractErrorMessage(err)}`);
    } finally {
      setSavingFeature(false);
    }
  };

  // Add this function to fetch grape varieties
  const fetchGrapeVarieties = useCallback(async () => {
    try {
      const response = await axios.get('/api/grape-varieties');
      setGrapeVarieties(response.data || []);
    } catch (err) {
      console.error('Error fetching grape varieties:', err);
      setGrapeVarieties([]);
    }
  }, []);

  // Add this function to handle adding a new grape variety
  const handleAddNewGrapeVariety = async () => {
    if (!newGrapeVariety.trim()) {
      return;
    }

    try {
      setSavingGrapeVariety(true);
      const response = await axios.post('/api/grape-varieties', {
        name: newGrapeVariety.trim(),
        description: ''
      });

      // Add the new grape variety to the list
      const newVarietyData = response.data;
      setGrapeVarieties(prev => [...prev, newVarietyData]);

      // Select the new grape variety
      const newVarietyOption = {
        value: newVarietyData.name,
        label: newVarietyData.name
      };
      setSelectedGrapeVarieties(prev => [...prev, newVarietyOption]);

      // Reset the form
      setNewGrapeVariety('');
      setAddingNewGrapeVariety(false);
    } catch (err: any) {
      console.error('Error creating new grape variety:', err);
      setError(`Nie udało się utworzyć nowej odmiany: ${extractErrorMessage(err)}`);
    } finally {
      setSavingGrapeVariety(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError('');

      const productData = {
        ...product,
        age_years: product.age_years || null, // Ensure age_years is sent, even if it's null
        awards: awards, // This should work now
        features: selectedFeatures.map(f => f.value),
        grape_variety: selectedGrapeVarieties.map(g => g.value),
        image_url: uploadedImageUrl || product.image_url || ''
      };

      // Add debug logging
      console.log('Submitting product data:', productData);
      console.log('Awards being sent:', awards);

      let response;
      if (isEditing) {
        response = await axios.put(`/api/products/${id}`, productData, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
      } else {
        response = await axios.post(`/api/products`, productData, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
      }

      console.log('Product saved successfully:', response.data);
      navigate('/');
    } catch (err: any) {
      console.error('Error saving product:', err);
      console.error('Error response data:', err.response?.data);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const isChecked = (e.target as HTMLInputElement).checked;

    setProduct(prev => ({
      ...prev,
      // A cleared number input reports value === '', which Pydantic rejects for
      // an Optional[float]. Send null instead: the update endpoint uses
      // exclude_unset, so an omitted key would silently keep the old value
      // whereas null actually clears it.
      [name]: type === 'checkbox'
        ? isChecked
        : type === 'number'
          ? (value === '' ? null : Number(value))
          : value
    }));
  };

  const handleDropOrPasteImage = async (file: File) => {
    // Create preview URL immediately
    const previewUrl = URL.createObjectURL(file);
    setUploadedImageUrl(previewUrl);

    // Upload the image
    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const res = await axios.post('/api/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        }
      });

      // Replace preview URL with actual uploaded URL
      URL.revokeObjectURL(previewUrl); // Clean up preview URL
      setUploadedImageUrl(res.data.file_path);
        // Update product with the new image URL
      setProduct(prev => ({
        ...prev,
        image_url: res.data.file_path
      }));} catch (err: any) {
      // On error, revert to no image and show error
      URL.revokeObjectURL(previewUrl);
      setUploadedImageUrl('');
      setError('Błąd podczas przesyłania zdjęcia');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleDropOrPasteImage(e.target.files[0]);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          const timestamp = Date.now();
          const extension = file.type.split('/')[1] || 'png';
          const properFile = new File([file], `pasted-image-${timestamp}.${extension}`, {
            type: file.type,
            lastModified: timestamp
          });

          handleDropOrPasteImage(properFile);
          break;
        }
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleDropOrPasteImage(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleAddAward = () => {
    if (currentAward.trim()) {
      setAwards(prev => [...prev, currentAward.trim()]);
      setCurrentAward('');
    }
  };

  const handleRemoveAward = (indexToRemove: number) => {
    setAwards(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleAwardInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentAward(e.target.value);
  };

  const handleAwardKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAward();
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    if (field === 'name') setProduct(prev => ({ ...prev, name: value }));
  };

  const handleProductTypeChange = (type: string) => {
    // Find the product type ID that corresponds to the type name
    const productTypeObj = productTypes.find(pt => pt.name.toLowerCase() === type.toLowerCase());

    if (productTypeObj) {
      setProduct(prev => ({
        ...prev,
        product_type_id: productTypeObj.id,
        // Reset dependent fields
        category_id: undefined,
        subcategory_id: undefined
      }));
    }

    setFilters(prev => ({ ...prev, productType: type }));
  };

  const fetchProductTypes = useCallback(async () => {
    try {
      const response = await axios.get('/api/product-types');
      setProductTypes(response.data);
    } catch (err) {
      console.error('Error fetching product types:', err);
    }
  }, []);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);

  const fetchCategories = useCallback(async (productTypeId: number) => {
    setLoadingCategories(true);
    if (!productTypeId) {
      setCategoryOptions([]);
      setSubcategoryOptions([]);
      return;
    }

    try {
      setLoading(true); // Add loading state
      console.log(`Fetching categories for product type ID: ${productTypeId}`);
      const response = await axios.get(`/api/categories?product_type_id=${productTypeId}`);
      console.log('Categories received:', response.data);
      setCategoryOptions(response.data || []);
      setSubcategoryOptions([]);
      return response.data;
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategoryOptions([]);
    } finally {
      setLoading(false);
      setLoadingCategories(false);
    }
  }, []);

  const fetchSubcategories = useCallback(async (categoryId: number) => {
    setLoadingSubcategories(true);
    if (!categoryId) {
      setSubcategoryOptions([]);
      return;
    }

    try {
      setLoading(true);
      console.log(`Fetching subcategories for category ID: ${categoryId}`);
      const response = await axios.get(`/api/subcategories?category_id=${categoryId}`);
      console.log('Subcategories received:', response.data);
      setSubcategoryOptions(response.data || []);
      return response.data;
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setSubcategoryOptions([]);
    } finally {
      setLoading(false);
      setLoadingSubcategories(false);
    }
  }, []);

  const getCategoryName = useCallback((categoryId: number | undefined) => {
    if (!categoryId) return '';
    const category = categoryOptions.find(cat => cat.id === categoryId);
    return category ? category.name : '';
  }, [categoryOptions]);

  const getSubcategoryName = useCallback((subcategoryId: number | undefined) => {
    if (!subcategoryId) return '';
    const subcategory = subcategoryOptions.find(sub => sub.id === subcategoryId);
    return subcategory ? subcategory.name : '';
  }, [subcategoryOptions]);

  const getProductTypeName = (typeId: number) => {
    const productType = productTypes.find(pt => pt.id === typeId);
    return productType ? productType.name : '';
  };

  const getProductTypeDisplayName = (typeName: string): string => {
    const nameMap: { [key: string]: string } = {
      'wine': 'Wino',
      'cigar': 'Cygaro',
      'other': 'Inne',
      'spirit': 'Wysokoprocentowe',
      'gift': 'Prezent'
    };

    return nameMap[typeName.toLowerCase()] || typeName;
  };

  // --- ALL useEffect HOOKS ---

  useEffect(() => {
    axios.defaults.headers.common['Accept'] = 'application/json';
    axios.defaults.headers.common['Content-Type'] = 'application/json';

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error);
        if (error.response?.headers['content-type']?.includes('text/html')) {
          console.error('Received HTML response instead of JSON.');
          error.message = 'Server returned HTML instead of JSON.';
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    if (isEditing) {
      fetchProduct();
    }
  }, [isEditing, fetchProduct]);

  useEffect(() => {
    // Remove the global paste handler entirely
    // We'll only handle pastes directly in the image drop area

    // Clean up function to prevent memory leaks
    return () => {
      // No need for cleanup as we're not adding any listeners
    };
  }, []);

  useEffect(() => {
    fetchProductTypes();
  }, [fetchProductTypes]);

  // Add this useEffect to fetch grape varieties on component mount
  useEffect(() => {
    fetchGrapeVarieties();
  }, [fetchGrapeVarieties]);

  useEffect(() => {
    if (product.product_type_id) {
      fetchCategories(product.product_type_id);
    }
  }, [product.product_type_id, fetchCategories]);

  useEffect(() => {
    if (product.category_id) {
      fetchSubcategories(product.category_id);
    }
  }, [product.category_id, fetchSubcategories]);

  useEffect(() => {
    if (product.product_type_id) {
      fetchProductFeatures(product.product_type_id);
    }
  }, [product.product_type_id, fetchProductFeatures]);

  // Add this useEffect after your other useEffects
useEffect(() => {
  // Only run this when editing an existing product and we have both the product data and category options
  if (isEditing && product.category_id && product.subcategory_id && categoryOptions.length > 0) {
    console.log('Initializing subcategories for existing product');
    console.log('Product subcategory_id:', product.subcategory_id);

    // Fetch subcategories for the existing product's category
    fetchSubcategories(product.category_id);
  }
}, [isEditing, product.category_id, product.subcategory_id, categoryOptions.length, fetchSubcategories]);

  // --- CONDITIONAL RETURN ---

  if (loading && isEditing) {
    return <div className="loading">Ładowanie danych produktu...</div>;
  }

  // Soft limit: longer text is saved, the counter just warns.
  const characteristicsLength =
    new DOMParser().parseFromString(product.characteristics || '', 'text/html').body.textContent?.length || 0;

  // --- JSX RENDER ---

  return (
    <div className="container">
      <div className="card">
        <div className="page-header">
          <h1>{isEditing ? 'Edytuj Produkt' : 'Dodaj Nowy Produkt'}</h1>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="product-form">
          <div className="form-section">
            <h3>Podstawowe informacje</h3>

            <div className="form-group">
              <label htmlFor="name">Nazwa produktu *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={product.name}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                required
                placeholder="np. Château Margaux 2015"
              />
            </div>
            <div className="form-group">
              <label htmlFor="brand">Marka/Producent</label>
              <input
                type="text"
                id="brand"
                name="brand"
                value={product.brand || ''}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="np. Bodega Teho"
              />
            </div>
            <div className="form-group">
              <label htmlFor="product_type_id">Typ produktu *</label>
              <select
                id="product_type_id"
                name="product_type_id"
                value={product.product_type_id}
                onChange={(e) => {
                  const newTypeId = Number(e.target.value);
                  // First clear dependencies
                  setProduct(prev => ({
                    ...prev,
                    product_type_id: newTypeId,
                    category_id: undefined,
                    subcategory_id: undefined
                  }));

                  // Then fetch categories (with a small delay to ensure state is updated)
                  setTimeout(() => fetchCategories(newTypeId), 50);
                }}
                required
              >
                <option value="">Wybierz typ produktu</option>
                {productTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {getProductTypeDisplayName(type.name)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="category_id">
                {product.product_type_id === 2 ? 'KOLOR WINA' : 'Kategoria'}
              </label>
              <div className="editor-row">
                {!addingNewCategory ? (
                  <>
                    <select
                      id="category_id"
                      name="category_id"
                      value={product.category_id || ''}
                      onChange={(e) => {
                        const newCategoryId = e.target.value ? Number(e.target.value) : undefined;

                        // First update state
                        setProduct(prev => ({
                          ...prev,
                          category_id: newCategoryId,
                          subcategory_id: undefined
                        }));

                        // Then fetch subcategories if there's a valid category ID
                        if (newCategoryId) {
                          setTimeout(() => fetchSubcategories(newCategoryId), 50);
                        }
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={!product.product_type_id || addingNewCategory || loadingCategories}
                      className="form-control editor-grow"

                    >
                      <option value="">
                        {loadingCategories
                          ? "Ładowanie..."
                          : (product.product_type_id === 2
                            ? 'Wybierz kolor wina'
                            : 'Wybierz kategorię')}
                      </option>
                      {categoryOptions.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => setAddingNewCategory(true)}
                      disabled={!product.product_type_id}
                    >
                      + Nowa
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Nazwa nowej kategorii"
                      className="form-control editor-grow"

                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // Prevent form submission
                          if (newCategoryName.trim() && !savingCategory) {
                            handleAddNewCategory();
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setAddingNewCategory(false);
                        setNewCategoryName('');
                      }}
                    >
                      Anuluj
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddNewCategory}
                      disabled={savingCategory || !newCategoryName.trim()}
                    >
                      {savingCategory ? 'Zapisywanie...' : 'Dodaj'}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="subcategory_id">
                {product.product_type_id === 2 ? 'RODZAJ WINA' : 'Podkategoria'}
              </label>
              <div className="editor-row">
                {!addingNewSubcategory ? (
                  <>
                    <select
                      id="subcategory_id"
                      name="subcategory_id"
                      value={product.subcategory_id || ''}
                      onChange={(e) => {
                        const newSubcategoryId = e.target.value ? Number(e.target.value) : undefined;
                        setProduct(prev => ({
                          ...prev,
                          subcategory_id: newSubcategoryId
                        }));
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={!product.category_id || addingNewSubcategory || loadingSubcategories}
                      className="form-control editor-grow"

                    >
                      <option value="">
                        {product.product_type_id === 2
                          ? 'Wybierz rodzaj wina'
                          : 'Wybierz podkategorię'}
                      </option>
                      {subcategoryOptions.map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => setAddingNewSubcategory(true)}
                      disabled={!product.category_id}
                    >
                      + Nowa
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      value={newSubcategoryName}
                      onChange={(e) => setNewSubcategoryName(e.target.value)}
                      placeholder="Nazwa nowej podkategorii"
                      className="form-control editor-grow"

                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // Prevent form submission
                          if (newSubcategoryName.trim() && !savingSubcategory) {
                            handleAddNewSubcategory();
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setAddingNewSubcategory(false);
                        setNewSubcategoryName('');
                      }}
                    >
                      Anuluj
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddNewSubcategory}
                      disabled={savingSubcategory || !newSubcategoryName.trim()}
                    >
                      {savingSubcategory ? 'Zapisywanie...' : 'Dodaj'}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Product Features Section - Moved to Basic Information */}
            <div className="form-group editor-block">
              <label>Cechy charakterystyczne</label>

              {!addingNewFeature ? (
                <div className="editor-row">
                  <div className="editor-grow">
                    <MultiSelect
                      options={productFeatures.map(feature => ({
                        label: feature.name,
                        value: feature.name
                      }))}
                      value={selectedFeatures}
                      onChange={setSelectedFeatures}
                      labelledBy="Wybierz cechy produktu"
                      hasSelectAll={false}
                      disableSearch={false}
                      className="multi-select-features"
                      ItemRenderer={CustomItemRenderer}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => setAddingNewFeature(true)}
                    disabled={!product.product_type_id}
                  >
                    + Nowa cecha
                  </button>
                </div>
              ) : (
                <div className="editor-stack">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Nazwa nowej cechy"
                    className="form-control"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault(); // Prevent form submission
                        if (newFeature.trim() && !savingFeature) {
                          handleAddNewFeature();
                        }
                      }
                    }}
                  />
                  <div className="editor-row">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setAddingNewFeature(false);
                        setNewFeature('');
                      }}
                    >
                      Anuluj
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddNewFeature}
                      disabled={savingFeature || !newFeature.trim()}
                    >
                      {savingFeature ? 'Zapisywanie...' : 'Dodaj'}
                    </button>
                  </div>
                </div>
              )}

              {selectedFeatures.length > 0 && (
                <div className="selected-features">
                  <div className="chip-list">
                    {selectedFeatures.map((feature, index) => (
                      <span key={index} className="chip">
                        {feature.label}
                        <button
                          type="button"
                          onClick={() => setSelectedFeatures(prev =>
                            prev.filter((_, i) => i !== index)
                          )}
                          className="chip__remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <small className="text-muted editor-hint">
                Wybierz cechy charakterystyczne dla tego produktu (np. Bio, Naturalne, Vegan)
              </small>
            </div>
          </div>
          <div className="form-section">
            <h3>Pochodzenie</h3>

            <div className="form-group">
              <AutocompleteField
                label="Kraj"
                value={product.country || ''}
                onChange={value => setProduct(prev => ({ ...prev, country: value }))}
                field="country"
                placeholder="np. Francja"
                endpoint="/api/search/products"
              />
            </div>

            <div className="form-group">
              <AutocompleteField
                label="Region"
                value={product.region || ''}
                onChange={value => setProduct(prev => ({ ...prev, region: value }))}
                field="region"
                placeholder="np. Bordeaux"
                endpoint="/api/search/products"
              />
            </div>

            {product.product_type_id === 2 && ( // Only show for Wine (ID 2)
              <>
                <div className="form-group">
                  <label htmlFor="appellation">Apelacja</label>
                  <input
                    type="text"
                    id="appellation"
                    name="appellation"
                    value={product.appellation || ''}
                    onChange={handleInputChange}
                    placeholder="np. Margaux AOC"
                  />
                </div>

                {/* START: Replace the old input with this new multi-select component */}
                <div className="form-group">
                  <label>Odmiana winogron</label>
                  {!addingNewGrapeVariety ? (
                    <div className="editor-row">
                      <div className="editor-grow">
                        <MultiSelect
                          options={grapeVarieties.map(g => ({ label: g.name, value: g.name }))}
                          value={selectedGrapeVarieties}
                          onChange={setSelectedGrapeVarieties}
                          labelledBy="Wybierz odmiany"
                          overrideStrings={{ "selectSomeItems": "Wybierz odmiany..." }}
                          hasSelectAll={false}
                          ItemRenderer={CustomItemRenderer}
                        />
                      </div>
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setAddingNewGrapeVariety(true)}>+</button>
                    </div>
                  ) : (
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nazwa nowej odmiany"
                        value={newGrapeVariety}
                        onChange={(e) => setNewGrapeVariety(e.target.value)}
                        autoFocus
                      />
                      <button className="btn btn-primary" type="button" onClick={handleAddNewGrapeVariety} disabled={savingGrapeVariety}>
                        {savingGrapeVariety ? 'Zapisywanie...' : 'Dodaj'}
                      </button>
                      <button className="btn btn-outline-secondary" type="button" onClick={() => setAddingNewGrapeVariety(false)}>Anuluj</button>
                    </div>
                  )}

                  {/* Add this block to display selected grape varieties as tags */}
                  {selectedGrapeVarieties.length > 0 && (
                    <div>
                      {/* .chip-list lays these out; the .selected-tags wrapper
                          this replaced was a leftover whose rule lives in the
                          filter panel's stylesheet and never applied here. */}
                      <div className="chip-list">
                        {selectedGrapeVarieties.map((grape, index) => (
                          <span key={index} className="chip">
                            {grape.label}
                            <button
                              type="button"
                              onClick={() => setSelectedGrapeVarieties(prev =>
                                prev.filter((_, i) => i !== index)
                              )}
                              className="chip__remove"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* END: Replacement code */}
              </>
            )}
          </div>
          <div className="form-section">
            <h3>Charakterystyka</h3>
            <div className="form-row">
              {/* Tylko dla win/alkoholi */}
              {product.product_type_id === 3 && ( // 3 = cigar
                <>
              <div className="form-group">
                <label htmlFor="wrapper">Wrapper</label>
                <input
                  type="text"
                  id="wrapper"
                  name="wrapper"
                  value={product.wrapper || ''}
                  onChange={handleInputChange}
                  placeholder="np. Connecticut Shade"
                />
              </div>

              <div className="form-group">
                <label htmlFor="length_mm">Długość (mm)</label>
                <input
                  type="number"
                  id="length_mm"
                  name="length_mm"
                  value={product.length_mm || ''}
                  onChange={handleInputChange}
                  placeholder="np. 150"

                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ring_gauge">Pierścień (Ring Gauge)</label>
                <input
                  type="number"
                  id="ring_gauge"
                  name="ring_gauge"
                  value={product.ring_gauge || ''}
                  onChange={handleInputChange}
                  placeholder="np. 52"

                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
              </>
              )}
              {product.product_type_id === 1 && ( // 1 = spirits
                <div className="form-group">
                  <label htmlFor="age_years">Wiek whisky (lata)</label>
                  <input
                    type="number"
                    id="age_years"
                    name="age_years"
                    value={product.age_years || ''}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="np. 18"

                    onWheel={(e) => e.currentTarget.blur()}
                  />
                </div>
              )}
              {product.product_type_id !== 3 && (
                <>
                  <div className="form-group">
                    <label htmlFor="alcohol_content">Zawartość alkoholu (%)</label>
                    <input
                      type="number"
                      id="alcohol_content"
                      name="alcohol_content"
                      value={product.alcohol_content || ''}
                      onChange={handleInputChange}
                      step="0.1"
                      min="0"
                      max="100"
                      placeholder="np. 13.5"

                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="volume">Pojemność (ml)</label>
                    <input
                      type="number"
                      id="volume"
                      name="volume"
                      value={product.volume || ''}
                      onChange={handleInputChange}
                      step="1"
                      min="1"
                      placeholder="np. 750"

                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="form-group editor-block">
              <label htmlFor="taste_profile">Profil smakowy</label>
              <textarea
                id="taste_profile"
                name="taste_profile"
                value={product.taste_profile || ''}
                onChange={handleInputChange}
                rows={5}
                className="editor-textarea"
                placeholder="Opisz aromat, smak i finisz..."
                maxLength={350}
              />
              <small className="text-muted editor-hint">
                {(product.taste_profile || '').length} / 350
              </small>
            </div>
<div className="form-group editor-block">
  <label htmlFor="characteristics">Charakterystyka</label>
  <ReactQuill
    id="characteristics"
    value={product.characteristics || ''}
    // Always accept the edit: rejecting it leaves Quill showing text that state
    // doesn't have, and react-quill reverts the editor on the next render.
    onChange={(content) => {
      setProduct(prev => ({
        ...prev,
        characteristics: content
      }));
    }}
    modules={{
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['clean']
      ]
    }}
    theme="snow"
    className="editor-quill"
    placeholder="Charakterystyka produktu..."
  />
  <small className={`text-muted editor-hint${characteristicsLength > CHARACTERISTICS_SOFT_LIMIT ? ' editor-hint--over' : ''}`}>
    {characteristicsLength} / {CHARACTERISTICS_SOFT_LIMIT}
    {characteristicsLength > CHARACTERISTICS_SOFT_LIMIT && (
      <> — przekroczono zalecany limit, tekst może nie zmieścić się na slajdzie</>
    )}
  </small>
</div>
          </div>
          <div className="form-section">
            <h3>Cena i zdjęcie</h3>

            <div className="editor-row editor-row--wide">
              <div className="form-group editor-grow">
                <label htmlFor="price_net">Cena podstawowa (netto PLN)</label>
                <input
                  type="number"
                  id="price_net"
                  name="price_net"
                  value={product.price_net || ''}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="np. 150.00"

                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>

              <div className="form-group editor-grow">
                <label htmlFor="price_alt">Cena alternatywna (netto PLN)</label>
                <input
                  type="number"
                  id="price_alt"
                  name="price_alt"
                  value={product.price_alt || ''}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="np. 180.00"

                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
            </div>

            <div className="form-group">
              {/* Only show the URL input if no uploaded image is present */}
              {!uploadedImageUrl && (
                <>
                  <label htmlFor="image_url">URL zdjęcia</label>
                  <input
                    type="url"
                    id="image_url"
                    name="image_url"
                    value={product.image_url || ''}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                  />
                </>
              )}

              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}

              />

              <div
                onPaste={(e) => {
                  // Only handle image pastes in this specific area
                  const items = e.clipboardData?.items;
                  if (!items) return;

                  for (let i = 0; i < items.length; i++) {
                    if (items[i].type.indexOf('image') !== -1) {
                      const file = items[i].getAsFile();
                      if (file) {
                        e.preventDefault();
                        handleDropOrPasteImage(file);
                        break;
                      }
                    }
                  }
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                tabIndex={0}
                className="image-dropzone"
                title="Kliknij, aby zaznaczyć obszar i wklej (Ctrl+V) zdjęcie tutaj"
              >
                {uploadedImageUrl ? (
                  <img
  src={
    uploadedImageUrl.startsWith('/')
      ? `${process.env.REACT_APP_BACKEND_URL || ''}${uploadedImageUrl}`
      : uploadedImageUrl
  }
  alt="Podgląd"
  className="editor-thumb"
/>
                ) : (
                  <span className="text-muted">Kliknij tutaj i wklej (Ctrl+V) zdjęcie</span>
                )}
              </div>
              {uploadedImageUrl && (
                <button
                  type="button"
                  className="btn btn-secondary"

                  onClick={() => {
                    setUploadedImageUrl('');
                  }}
                >
                  Usuń zdjęcie
                </button>
              )}
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={product.is_active}
                  onChange={handleInputChange}
                />
                Produkt aktywny
              </label>
            </div>
          </div>

          {/* Awards section */}
          <div className="form-section">
            <h3>Nagrody i punktacje</h3>

            <div className="awards-list">
              {awards.map((award, index) => (
                <div key={index} className="award-item">
                  <span>{award}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAward(index)}
                    className="btn btn-sm btn-danger"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div className="form-group editor-row">
              <input
                type="text"
                className="form-control"
                placeholder="np. Vivino: 4,0"
                value={currentAward}
                onChange={handleAwardInputChange}
                onKeyPress={handleAwardKeyPress}
              />
              <button
                type="button"
                className="btn btn-primary ml-2"
                onClick={handleAddAward}
              >
                Dodaj
              </button>
            </div>
            <small className="text-muted">
              Dodaj nagrody, punktacje lub wyróżnienia produktu (np. "Vivino: 4.0", "Wine Align 92/100")
            </small>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
              Anuluj
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Zapisywanie...' : isEditing ? 'Aktualizuj' : 'Dodaj produkt'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default ProductEditor;
